import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ProjectDatabaseCredentials } from '@claude-code-api/shared';
import { Badge, Button } from '@/components/ui';
import {
  ChevronDown,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { DatabaseDetail } from './DatabaseDetail';

export interface DatabaseInfo {
  id: string;
  name: string;
  type: 'project' | 'standalone';
  projectId?: string;
  projectName?: string;
  credentials: ProjectDatabaseCredentials | null;
  tables: DatabaseTable[];
  loadingCredentials: boolean;
  loadingTables: boolean;
}

export interface DatabaseTable {
  name: string;
  type: 'table' | 'view';
  sizeBytes: number;
  columnCount: number;
}

interface DatabaseListProps {
  databases: DatabaseInfo[];
  onDelete: (db: DatabaseInfo) => void;
  onUpdateDatabase: (id: string, updates: Partial<DatabaseInfo>) => void;
}

export function DatabaseList({ databases, onDelete, onUpdateDatabase }: DatabaseListProps) {
  const { t } = useTranslation('databases');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (dbId: string) => {
    setExpandedId((prev) => (prev === dbId ? null : dbId));
  };

  return (
    <div className="space-y-3">
      {databases.map((db) => (
        <div key={db.id} className="bg-card border rounded-lg overflow-hidden">
          <div
            role="button"
            tabIndex={0}
            onClick={() => toggleExpand(db.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(db.id); } }}
            className="w-full p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors duration-150"
            aria-expanded={expandedId === db.id}
          >
            <div className="flex items-center gap-3">
              <span className="font-semibold">{db.name}</span>
              {db.type === 'project' ? (
                <Badge variant="outline">{t('types.project')}</Badge>
              ) : (
                <Badge variant="secondary">{t('types.standalone')}</Badge>
              )}
              {db.projectName && db.type === 'project' && (
                <span className="text-sm text-muted-foreground">{db.projectName}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {db.type === 'project' ? (
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Link to={`/projects/${db.projectId}`}>
                    <ExternalLink className="w-4 h-4 mr-1" />
                    {t('actions.viewProject')}
                  </Link>
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(db);
                  }}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t('actions.delete')}
                </Button>
              )}
              <ChevronDown
                className={`w-5 h-5 text-muted-foreground transition-transform duration-150 ${
                  expandedId === db.id ? 'rotate-180' : ''
                }`}
              />
            </div>
          </div>

          {expandedId === db.id && (
            <div className="border-t">
              <DatabaseDetail
                database={db}
                onUpdateDatabase={onUpdateDatabase}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
